import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { dequal } from "dequal";
import type { RootState } from "@/store/store";
import type { InfoPopoverProps } from "./InfoPopover.types";
import ICONS from "@/constant/IconManager";
import "./InfoPopover.styles.css";

const CLOSE_ANIMATION_DURATION = 200; // ms

const getIconSrc = (iconKey: string | undefined): string | undefined => {
  if (!iconKey || !ICONS[iconKey]) return undefined;
  return ICONS[iconKey].dark || ICONS[iconKey].light;
};

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  caption?: string;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt,
  caption,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (imageError) {
    return null;
  }

  return (
    <div className="info-popover-image-wrapper">
      <img
        src={src}
        alt={alt}
        className={`info-popover-section-image ${
          imageLoaded ? "info-popover-image-visible" : "info-popover-image-hidden"
        }`}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
      {!imageLoaded && !imageError && (
        <div className="info-popover-image-loading">
          <div className="info-popover-image-loading-spinner" />
        </div>
      )}
      {caption && imageLoaded && (
        <p className="info-popover-image-caption">{caption}</p>
      )}
    </div>
  );
};

const MemoizedImageWithFallback = React.memo(ImageWithFallback);

const InfoPopover: React.FC<InfoPopoverProps> = ({
  isOpen,
  onClose,
  title,
  content,
  iconKey,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);

  const featureStatus = useSelector(
    (state: RootState) => state.featureStatus,
    (prev, next) => dequal(prev, next)
  );

  const isLegacyEnabled = featureStatus?.legacyScore?.enabled ?? false;

  const visibleSections = useMemo(() => {
    if (!content.sections) return [];
    return content.sections.filter((section) => {
      if (section.showIfLegacyEnabled && !isLegacyEnabled) {
        return false;
      }
      return true;
    });
  }, [content.sections, isLegacyEnabled]);

  const markdownComponents = useMemo(
    () => ({
      p: ({ ...props }: React.ComponentProps<"p">) => (
        <p className="info-popover-markdown-paragraph" {...props} />
      ),
      strong: ({ ...props }: React.ComponentProps<"strong">) => (
        <strong className="info-popover-markdown-strong" {...props} />
      ),
      em: ({ ...props }: React.ComponentProps<"em">) => (
        <em className="info-popover-markdown-em" {...props} />
      ),
      ul: ({ ...props }: React.ComponentProps<"ul">) => (
        <ul className="info-popover-markdown-list" {...props} />
      ),
      ol: ({ ...props }: React.ComponentProps<"ol">) => (
        <ol className="info-popover-markdown-list" {...props} />
      ),
      li: ({ ...props }: React.ComponentProps<"li">) => (
        <li className="info-popover-markdown-list-item" {...props} />
      ),
    }),
    []
  );

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, CLOSE_ANIMATION_DURATION);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      // Prevent body scroll when popover is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Focus the container for accessibility
      containerRef.current?.focus();
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, handleClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === overlayRef.current) {
      handleClose();
    }
  };

  if (!isOpen && !isClosing) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      className={`info-popover-overlay ${isClosing ? "closing" : ""}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="info-popover-title"
      aria-describedby="info-popover-content"
    >
      <div
        ref={containerRef}
        className="info-popover-container"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="info-popover-header">
          <div className="info-popover-title-wrapper">
            {iconKey && getIconSrc(iconKey) && (
              <img
                src={getIconSrc(iconKey)!}
                alt=""
                className="info-popover-title-icon"
                aria-hidden="true"
              />
            )}
            <h2 id="info-popover-title" className="info-popover-title">
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="info-popover-close-button"
            onClick={handleClose}
            aria-label="Close documentation"
          >
            <svg
              className="info-popover-close-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div id="info-popover-content" className="info-popover-content">
          {content.overview && (
            <div className="info-popover-overview">
              <ReactMarkdown components={markdownComponents}>
                {content.overview}
              </ReactMarkdown>
            </div>
          )}

          {visibleSections && visibleSections.length > 0 && (
            <>
              {visibleSections.map((section, index) => (
                <React.Fragment key={`section-${section.title}`}>
                  <div className="info-popover-section">
                    {section.title && (
                      <div className="info-popover-section-title-wrapper">
                        {section.iconKey && ICONS[section.iconKey]?.light && (
                          <img
                            src={ICONS[section.iconKey].light}
                            alt=""
                            className="info-popover-section-icon"
                          />
                        )}
                        <h3 className="info-popover-section-title">
                          {section.title}
                        </h3>
                      </div>
                    )}
                    {section.content && (
                      <div className="info-popover-section-content">
                        <ReactMarkdown components={markdownComponents}>
                          {section.content}
                        </ReactMarkdown>
                      </div>
                    )}
                    {section.imageUrl && (
                      <MemoizedImageWithFallback
                        src={section.imageUrl}
                        alt={section.title || "Documentation image"}
                        caption={section.imageCaption}
                      />
                    )}
                  </div>
                  {index < visibleSections.length - 1 && (
                    <div className="info-popover-section-divider" />
                  )}
                </React.Fragment>
              ))}
            </>
          )}

          {content.metrics && content.metrics.length > 0 && (
            <div className="info-popover-metrics">
              <h3 className="info-popover-metrics-title">Metrics</h3>
              <div className="info-popover-metrics-list">
                {content.metrics.map((metric) => (
                  <div key={`metric-${metric.name}`} className="info-popover-metric">
                    <div className="info-popover-metric-header">
                      {metric.iconKey && ICONS[metric.iconKey]?.light && (
                        <img
                          src={ICONS[metric.iconKey].light}
                          alt=""
                          className="info-popover-metric-icon"
                        />
                      )}
                      <h4 className="info-popover-metric-name">
                        {metric.name}
                      </h4>
                    </div>
                    <div className="info-popover-metric-description">
                      <ReactMarkdown
                        components={{
                          p: ({ node, ...props }) => (
                            <p
                              className="info-popover-markdown-paragraph"
                              {...props}
                            />
                          ),
                          strong: ({ node, ...props }) => (
                            <strong
                              className="info-popover-markdown-strong"
                              {...props}
                            />
                          ),
                          em: ({ node, ...props }) => (
                            <em
                              className="info-popover-markdown-em"
                              {...props}
                            />
                          ),
                        }}
                      >
                        {metric.description}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoPopover;
